/**
 * AsHovercard Component
 * 
 * A simple container component for rich hovercard content.
 * This component is designed to be used as a child of as-hovercard-wrapper.
 * 
 * Features:
 * - Simple container for any HTML content
 * - Basic styling and positioning support
 * - Responsive design that adapts to content
 * - Light DOM implementation for better performance
 * - Optional close button in top-right corner
 * 
 * Attributes:
 * - `show-close`: Shows the close button when present (hidden by default)
 * 
 * CSS Custom Properties:
 * - `--hovercard-bg`: Background color (default: white)
 * - `--hovercard-border`: Border (default: 1px solid #ccc)
 * - `--hovercard-border-radius`: Border radius (default: 8px)
 * - `--hovercard-box-shadow`: Box shadow (default: 0 4px 12px rgba(0, 0, 0, 0.15))
 * - `--hovercard-padding`: Padding (default: 0)
 * - `--hovercard-max-width`: Maximum width (default: none)
 * - `--hovercard-min-width`: Minimum width (default: none)
 * - `--hovercard-close-button-size`: Close button size (default: 24px)
 * - `--hovercard-close-button-color`: Close button color (default: #666)
 * - `--hovercard-close-button-hover-color`: Close button hover color (default: #333)
 * 
 * Usage:
 * ```html
 * <!-- Without close button (default) -->
 * <as-hovercard>
 *   <div class="custom-content">
 *     <img src="image.jpg" alt="Preview"/>
 *     <p>Rich content goes here</p>
 *   </div>
 * </as-hovercard>
 *
 * <!-- With close button -->
 * <as-hovercard show-close>
 *   <div class="custom-content">Content with close button</div>
 * </as-hovercard>
 * ```
 */

export class AsHovercard extends HTMLElement {
  private static styleSheet: CSSStyleSheet | null = null;
  private closeButton: HTMLButtonElement | null = null;

  constructor() {
    super();
    this.applyStyles();
  }

  connectedCallback() {
    this.setupCloseButton();
  }

  disconnectedCallback() {
    this.removeCloseButton();
  }

  /**
   * Check if close button should be shown based on attribute
   */
  private shouldShowCloseButton(): boolean {
    return this.hasAttribute('show-close');
  }

  /**
   * Setup the close button if it should be shown
   */
  private setupCloseButton(): void {
    if (!this.shouldShowCloseButton()) return;

    // Create close button
    this.closeButton = document.createElement('button');
    this.closeButton.className = 'as-hovercard-close';
    this.closeButton.innerHTML = '✕';
    this.closeButton.setAttribute('aria-label', 'Close');
    this.closeButton.type = 'button';

    // Add click handler
    this.closeButton.addEventListener('click', this.handleCloseClick.bind(this));

    // Insert as first child
    this.insertBefore(this.closeButton, this.firstChild);
  }

  /**
   * Handle close button click
   */
  private handleCloseClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    
    // Dispatch close event that parent wrapper can listen to
    const closeEvent = new CustomEvent('as-hovercard-close', {
      bubbles: true,
      detail: { source: this }
    });
    this.dispatchEvent(closeEvent);
  }

  /**
   * Remove close button
   */
  private removeCloseButton(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.handleCloseClick.bind(this));
      if (this.closeButton.parentNode === this) {
        this.removeChild(this.closeButton);
      }
      this.closeButton = null;
    }
  }

  /**
   * Handle attribute changes
   */
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'show-close') {
      this.removeCloseButton();
      this.setupCloseButton();
    }
  }

  static get observedAttributes(): string[] {
    return ['show-close'];
  }

  /**
   * Create shared stylesheet for all hovercard instances
   */
  private static createStyleSheet(): CSSStyleSheet {
    if (this.styleSheet) return this.styleSheet;

    const sheet = new CSSStyleSheet();
    sheet.replaceSync(`
      as-hovercard {
        display: block;
        position: relative;
        background: var(--hovercard-bg, white);
        border: var(--hovercard-border, 1px solid #ccc);
        border-radius: var(--hovercard-border-radius, 8px);
        box-shadow: var(--hovercard-box-shadow, 0 4px 12px rgba(0, 0, 0, 0.15));
        padding: var(--hovercard-padding, 0);
        max-width: var(--hovercard-max-width, none);
        min-width: var(--hovercard-min-width, none);
        box-sizing: border-box;
        font-family: inherit;
        line-height: 1.4;
      }
      
      as-hovercard img {
        display: block;
        max-width: 100%;
        height: auto;
      }

      .as-hovercard-close {
        position: absolute;
        top: 0;
        right: 0;
        width: var(--hovercard-close-button-size, 24px);
        height: var(--hovercard-close-button-size, 24px);
        min-width: var(--hovercard-close-button-size, 24px);
        min-height: var(--hovercard-close-button-size, 24px);
        max-width: var(--hovercard-close-button-size, 24px);
        max-height: var(--hovercard-close-button-size, 24px);
        border: none;
        background: var(--hovercard-close-button-bg, rgba(0, 0, 0, 0.6));
        color: var(--hovercard-close-button-color, white);
        font-size: 14px;
        font-weight: normal;
        line-height: 1;
        font-family: Arial, sans-serif;
        cursor: pointer;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        z-index: 10;
        transform: translate(50%, -50%);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        box-sizing: border-box;
        padding: 0;
        aspect-ratio: 1 / 1;
      }

      .as-hovercard-close:hover {
        color: var(--hovercard-close-button-hover-color, white);
        background: var(--hovercard-close-button-hover-bg, rgba(0, 0, 0, 0.8));
        transform: translate(50%, -50%) scale(1.1);
      }

      .as-hovercard-close:focus {
        outline: 2px solid #007bff;
        outline-offset: 2px;
      }
    `);

    this.styleSheet = sheet;
    return sheet;
  }

  /**
   * Apply styles to the document if not already applied
   */
  private applyStyles(): void {
    const styleSheet = AsHovercard.createStyleSheet();
    
    // Add stylesheet to document if not already present
    if (!document.adoptedStyleSheets.includes(styleSheet)) {
      document.adoptedStyleSheets = [...document.adoptedStyleSheets, styleSheet];
    }
  }
}

customElements.define('as-hovercard', AsHovercard); 